import { useMutation, useQuery } from "@apollo/client";
import { CheckIcon } from "@heroicons/react/24/solid";
import { useState } from "react";

import { Button, getInput, Modal } from "../../../../../components/ui";
import { CREATE_REPORT_JOB_EMAIL_ATTACHMENT } from "../../../../../graphql/mutation/ReportJob";
import { FETCH_EMAIL_REPORT_JOBS } from "../../../../../graphql/query/ReportJob";
import { ALLOWED_DOCUMENT_UPLOAD_ACCEPT } from "../../../../../util/allowedDocumentUploadAccept";
import useFileUpload from "../../../../../hooks/useFileUpload";
import ReportJobSummary from "../../../../Reporting/components/ReportJobSummary";
import { mergeAttachments } from "./attachmentUtils";
import { useDebounce } from "../../../../../util";

const ImageField = props => {
  const { value, setValue, isMultiSelect } = props || {};
  const { uploadFile, loading } = useFileUpload();
  const handleUpload = async e => {
    const files = e.target.files;
    e.preventDefault();
    if (files.length > 1 && isMultiSelect) {
      const newArray = [];
      for (let i = 0; i < files.length; i++) {
        const result = await uploadFile({ file: files[i] });
        if (result.success) {
          newArray.push({ filename: result.filename, size: files[i].size });
        }
      }
      if (value) {
        setValue?.(mergeAttachments(value, newArray));
      } else {
        setValue?.(newArray);
      }
    } else {
      const result = await uploadFile({ file: files[0] });
      if (result.success) {
        const entry = { filename: result.filename, size: files[0].size };
        if (value) {
          setValue?.(mergeAttachments(value, [entry]));
        } else {
          setValue?.([entry]);
        }
      }
    }
  };

  const handleDetach = attachment => {
    setValue?.(value.filter(item => item?.filename !== attachment?.filename));
  };

  const inputProps = {
    ...props,
    loading,
    onChange: handleUpload,
    handleDetach,
  };

  return <div className="flex">{getInput(inputProps)}</div>;
};

const Content = props => {
  const { formik, isEmailContactContent = false } = props || {};
  const [selectedReportJobs, setSelectedReportJobs] = useState([]);
  const [attachmentError, setAttachmentError] = useState(null);
  const [reportSearch, setReportSearch] = useState("");
  const [reportOffset, setReportOffset] = useState(0);
  const reportLimit = 25;
  const reportFilters = useDebounce({ search: reportSearch, offset: reportOffset }, 300);
  const {
    data: reportJobData,
    loading: reportJobsLoading,
    error: reportJobsError,
  } = useQuery(FETCH_EMAIL_REPORT_JOBS, {
    skip: isEmailContactContent,
    variables: {
      input: {
        status: ["complete"],
        reports: ["brochure"],
        format: "pdf",
        limit: reportLimit,
        ...reportFilters,
      },
    },
    fetchPolicy: "cache-and-network",
  });
  const [createReportJobEmailAttachment, { loading: attachmentLoading }] =
    useMutation(CREATE_REPORT_JOB_EMAIL_ATTACHMENT);
  const reportJobs = reportJobData?.reportJobs?.edges || [];

  const mailMergeOptions = [
    { key: "forename", value: "{{forename}}", text: "Forename" },
    { key: "surname", value: "{{surname}}", text: "Surname" },
    { key: "company", value: "{{company}}", text: "Company" },
    { key: "title", value: "{{title}}", text: "Title" },
    { key: "salutation", value: "{{salutation}}", text: "Salutation" },
    { key: "greeting", value: "{{greeting}}", text: "Greetings" },
  ];

  const inputs = [
    !isEmailContactContent && {
      name: "title",
      label: "Mailing Title",
      type: "text",
      className: "sm:col-span-1",
    },
    {
      name: "meta.subject",
      label: "Subject",
      type: "text",
      className: "sm:col-span-1",
    },
    !isEmailContactContent && {
      name: "meta.preheader",
      label: "Pre-Header Text - Appears after subject line in inbox",
      type: "text",
      className: "sm:col-span-1",
    },
    {
      name: "meta.body",
      label: "Body",
      type: "ckeditortext",
      mailMergeOptions,
      className: "sm:col-span-2",
    },
  ];

  const imageInput = [
    {
      name: "meta.files",
      label: "Attach File(s)",
      type: "img",
      accept: ALLOWED_DOCUMENT_UPLOAD_ACCEPT,
      isMultiSelect: true,
      uploadType: "file(s)",
      maxSize: 15,
    },
  ];

  const renderImages = imageInput?.map((item, index) => {
    const inputProps = {
      ...item,
      ...formik?.getFieldProps(`${item.name}`),
      ...formik?.getFieldHelpers(`${item.name}`),
    };
    return <ImageField key={index} {...inputProps} />;
  });

  const renderInputs = inputs.filter(Boolean).map((item, index) => {
    const inputProps = {
      formik,
      showErrorOnBlur: true,
      ...item,
      ...formik?.getFieldProps(item.name),
    };

    return (
      <div key={index} className="grid flex-1 grid-cols-1 sm:grid-cols-2">
        {getInput(inputProps)}
      </div>
    );
  });

  const handleReportJobSelection = id =>
    setSelectedReportJobs(currentSelection =>
      currentSelection.includes(id)
        ? currentSelection.filter(reportJobId => reportJobId !== id)
        : [...currentSelection, id],
    );

  const attachSelectedReports = async closeModal => {
    if (selectedReportJobs.length === 0) return;

    setAttachmentError(null);
    try {
      const responses = await Promise.all(
        selectedReportJobs.map(id =>
          createReportJobEmailAttachment({ variables: { id } }),
        ),
      );
      const results = responses.map(
        response => response?.data?.createReportJobEmailAttachment,
      );
      const failedResult = results.find(result => !result?.success);
      if (failedResult) {
        throw new Error(
          failedResult.error || "Failed to attach the selected PDF",
        );
      }

      const newAttachments = results.map(({ filename, size }) => ({
        filename,
        size,
      }));
      formik?.setFieldValue?.(
        "meta.files",
        mergeAttachments(formik?.values?.meta?.files || [], newAttachments),
      );
      setSelectedReportJobs([]);
      closeModal?.();
    } catch (error) {
      setAttachmentError(error.message || "Failed to attach the selected PDF");
    }
  };

  const modalProps = {
    scale: "lg",
    renderBody: ({ closeModal }) => (
      <>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
          <p className="font-bold">Select generated PDF</p>
          <div>
            <Button
              className="mr-3"
              label="Cancel"
              onClick={() => closeModal?.()}
            />
            <Button
              label={attachmentLoading ? "Attaching" : "Attach"}
              disabled={attachmentLoading || selectedReportJobs.length === 0}
              onClick={() => attachSelectedReports(closeModal)}
            />
          </div>
        </div>
        <label className="mt-4 block">
          <span className="mb-2 block text-sm font-medium">Search generated PDFs</span>
          <input
            type="search"
            className="w-full rounded border border-gray-300 px-3 py-2"
            placeholder="Artist, work title, JJ reference, filename, year, medium or series"
            value={reportSearch}
            onChange={event => {
              setReportSearch(event.target.value);
              setReportOffset(0);
            }}
          />
        </label>
        {attachmentError ? (
          <p className="mt-3 text-sm text-red-600">{attachmentError}</p>
        ) : null}
        {reportJobsError ? (
          <p className="mt-3 text-sm text-red-600">
            Failed to load generated PDFs: {reportJobsError.message}
          </p>
        ) : null}
        {reportJobsLoading && reportJobs.length === 0 ? (
          <p className="mt-6 text-sm text-gray-500">Loading generated PDFs…</p>
        ) : null}
        {!reportJobsLoading && reportJobs.length === 0 && !reportJobsError ? (
          <p className="mt-6 text-sm text-gray-500">
            {reportSearch.trim()
              ? "No PDFs match your search."
              : "No completed PDFs are available. Generate one in PDF Generator first."}
          </p>
        ) : null}
        {reportJobs.length > 0 ? (
          <div className="mt-4 max-h-[60vh] overflow-auto">
            <table className="w-full min-w-[600px] border-collapse text-left">
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <th className="w-12 py-2 pr-3" />
                  <th className="px-3 py-2">PDF</th>
                  <th className="px-3 py-2">Filename</th>
                  <th className="py-2 pl-3">Completed</th>
                </tr>
              </thead>
              <tbody>
                {reportJobs.map(reportJob => {
                  const selected = selectedReportJobs.includes(reportJob.id);
                  return (
                    <tr
                      key={reportJob.id}
                      className="border-b border-gray-100 text-sm"
                    >
                      <td className="py-3 pr-3 align-top">
                        <button
                          type="button"
                          className="inline-flex h-5 w-5 items-center justify-center rounded border-2 border-black"
                          aria-label={`${selected ? "Deselect" : "Select"} ${
                            reportJob.filename || "generated PDF"
                          }`}
                          onClick={() => handleReportJobSelection(reportJob.id)}
                        >
                          {selected ? <CheckIcon className="h-4 w-4" /> : null}
                        </button>
                      </td>
                      <td className="px-3 py-3 align-top">
                        <ReportJobSummary job={reportJob} />
                      </td>
                      <td className="px-3 py-3 align-top text-gray-500">
                        {reportJob.filename}
                      </td>
                      <td className="py-3 pl-3 align-top text-gray-500">
                        {reportJob.completedAt
                          ? new Date(reportJob.completedAt).toLocaleDateString()
                          : ""}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
        <div className="mt-4 flex items-center justify-between gap-3">
          <span className="text-sm text-gray-500">
            {reportJobData?.reportJobs?.count || 0} PDFs · {selectedReportJobs.length} selected
          </span>
          <div className="flex gap-3">
            <Button label="Previous" disabled={reportJobsLoading || reportOffset === 0}
              onClick={() => setReportOffset(offset => Math.max(0, offset - reportLimit))} />
            <Button label="Next" disabled={reportJobsLoading || reportOffset + reportLimit >= (reportJobData?.reportJobs?.count || 0)}
              onClick={() => setReportOffset(offset => offset + reportLimit)} />
          </div>
        </div>
      </>
    ),
  };

  return (
    <div className="grid flex-1 grid-cols-1 gap-8">
      {renderInputs}
      {!isEmailContactContent && (
        <div className="flex flex-col gap-6 sm:flex-row">
          <div>{renderImages}</div>
          <div>
            <label className="mb-2 block text-xs font-medium text-black">
              Attach generated PDF(s)
            </label>
            <Modal {...modalProps}>
              <Button label="Select generated PDF" />
            </Modal>
          </div>
        </div>
      )}
    </div>
  );
};

export default Content;
